'use client'

import { useTranslations } from 'next-intl'

import type { NavGroup } from '@graftcode/design-system'

import { LAYOUT_TEST_IDS } from '@common/common-test-ids.const'
import { DEFAULT_HELP_ITEMS } from '@common/components/HelpMenu'
import { AppVariant } from '@common/enums'

import { useLayoutStore } from '@stores/layout/layout.store'

import { AcademyMobileMenu } from '../MobileMenu/variants/AcademyMobileMenu'
import { ConferenceMobileMenu } from '../MobileMenu/variants/ConferenceMobileMenu'
import { GatewayMobileMenu } from '../MobileMenu/variants/GatewayMobileMenu'
import { ModulesMobileMenu } from '../MobileMenu/variants/ModulesMobileMenu'
import { PortalMobileMenu } from '../MobileMenu/variants/PortalMobileMenu'
import { VisionMobileMenu } from '../MobileMenu/variants/VisionMobileMenu'

import { LayoutMobileMenuNavigationProvider } from './context/LayoutMobileMenuNavigationContext'
import { useLayoutMobileMenuNavigation } from './hooks/useLayoutMobileMenuNavigation'

type LayoutMobileMenuProps = {
  appVariant: AppVariant
  hideAddNew?: boolean
  menuGroups: NavGroup[]
  onNotificationClick?: () => void
}

export function LayoutMobileMenu({
  appVariant,
  hideAddNew = false,
  menuGroups,
  onNotificationClick,
}: LayoutMobileMenuProps) {
  const t = useTranslations('core')
  const isPortal = appVariant === AppVariant.PORTAL
  const isOpen = useLayoutStore((state) => state.mobileMenuOpen)
  const closeMobileMenu = useLayoutStore((state) => state.closeMobileMenu)
  const navigation = useLayoutMobileMenuNavigation({ isPortal, onNotificationClick })

  const shellProps = {
    isOpen,
    onClose: closeMobileMenu,
    menuGroups,
    helpItems: DEFAULT_HELP_ITEMS,
  }

  return (
    <div data-testid={LAYOUT_TEST_IDS.layoutMobileMenuHost} style={{ display: 'contents' }}>
      <LayoutMobileMenuNavigationProvider value={navigation}>
        {(() => {
          switch (appVariant) {
            case AppVariant.ACADEMY:
              return (
                <AcademyMobileMenu
                  isOpen={isOpen}
                  onClose={closeMobileMenu}
                  onNavigationClick={navigation.handleNavigationClick}
                />
              )
            case AppVariant.PORTAL:
              return <PortalMobileMenu {...shellProps} addNewLabel={t('sidebar.addNew')} hideAddNew={hideAddNew} />
            case AppVariant.VISION:
              return <VisionMobileMenu {...shellProps} />
            case AppVariant.GATEWAY:
              return <GatewayMobileMenu {...shellProps} />
            case AppVariant.MODULES:
              return <ModulesMobileMenu {...shellProps} />
            case AppVariant.CONFERENCE:
              return <ConferenceMobileMenu {...shellProps} />
            default:
              return <PortalMobileMenu {...shellProps} addNewLabel={t('sidebar.addNew')} hideAddNew={hideAddNew} />
          }
        })()}
      </LayoutMobileMenuNavigationProvider>
    </div>
  )
}
